import { ThemeConfig, ThemeColors } from '@/types/theme';
import { getContrastingTextColor } from './themeUtils';

/**
 * Fix a theme by ensuring all required foreground colors are present
 * @param theme - The theme to fix
 * @returns Fixed theme with all foreground colors
 */
export const fixThemeForegrounds = (theme: ThemeConfig): ThemeConfig => {
  const fixedTheme = { ...theme };
  const colors = { ...theme.colors };

  // Fix primary foreground
  if (!colors.primaryForeground) {
    colors.primaryForeground = getContrastingTextColor(colors.primary);
  }

  // Fix secondary foreground
  if (!colors.secondaryForeground) {
    colors.secondaryForeground = getContrastingTextColor(colors.secondary);
  }

  // Fix accent foreground
  if (!colors.accentForeground) {
    colors.accentForeground = getContrastingTextColor(colors.accent);
  }

  // Fix destructive foreground
  if (!colors.destructiveForeground) {
    colors.destructiveForeground = getContrastingTextColor(colors.destructive);
  }

  // Fix sidebar text colors
  if (!colors.sidebarTextColor) {
    colors.sidebarTextColor = getContrastingTextColor(colors.sidebarColor);
  }

  if (!colors.sidebarHoverTextColor) {
    colors.sidebarHoverTextColor = getContrastingTextColor(colors.sidebarHoverBgColor);
  }

  fixedTheme.colors = colors;
  return fixedTheme;
};

/**
 * Fix all preset themes to ensure they have proper foreground colors
 * @param themes - Array of themes to fix
 * @returns Array of fixed themes
 */
export const fixAllThemes = (themes: ThemeConfig[]): ThemeConfig[] => {
  return themes.map(theme => fixThemeForegrounds(theme));
};

/**
 * Check if a theme needs fixing (missing foreground colors)
 * @param theme - The theme to check
 * @returns True if the theme needs fixing
 */
export const needsThemeFix = (theme: ThemeConfig): boolean => {
  const { colors } = theme;
  
  return !colors.primaryForeground || 
         !colors.secondaryForeground || 
         !colors.accentForeground || 
         !colors.destructiveForeground ||
         !colors.sidebarTextColor ||
         !colors.sidebarHoverTextColor;
};

/**
 * Get a list of missing foreground colors for a theme
 * @param theme - The theme to check
 * @returns Array of missing foreground color names
 */
export const getMissingForegrounds = (theme: ThemeConfig): string[] => {
  const { colors } = theme;
  const missing: string[] = [];

  if (!colors.primaryForeground) missing.push('primaryForeground');
  if (!colors.secondaryForeground) missing.push('secondaryForeground');
  if (!colors.accentForeground) missing.push('accentForeground');
  if (!colors.destructiveForeground) missing.push('destructiveForeground');
  if (!colors.sidebarTextColor) missing.push('sidebarTextColor');
  if (!colors.sidebarHoverTextColor) missing.push('sidebarHoverTextColor');

  return missing;
}; 